import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// httpOnly cookie carries the JWT — `withCredentials` tells axios to
// send it along on every request. The token is NEVER accessible to JS
// (no more `localStorage.tf_token`), which eliminates XSS-based session
// hijacking as an attack vector.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach active role/branch selection from localStorage. These are UI
// preferences (which sidebar filter is active), NOT security tokens —
// safe to keep in localStorage.
api.interceptors.request.use((config) => {
  const activeRole = localStorage.getItem("tf_active_role");
  const activeBranch = localStorage.getItem("tf_active_branch");
  if (activeRole) config.headers["X-Active-Role"] = activeRole;
  if (activeBranch) config.headers["X-Active-Branch"] = activeBranch;
  return config;
});

// Phase 4-b — if the backend reports the account's app-access has
// been deactivated (401 or 403 with the sentinel phrase), boot the
// current tab straight to /login with a friendly notice. This is what
// makes "deactivating an employee terminates their live session"
// actually terminate the session at the next authenticated call.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";
    if ((status === 401 || status === 403) && typeof detail === "string" &&
        /application access has been deactivated/i.test(detail)) {
      try {
        localStorage.removeItem("tf_active_role");
        localStorage.removeItem("tf_active_branch");
      } catch { /* noop */ }
      // Full reload guarantees any in-memory state is torn down. The
      // stale access_token cookie will be cleared by the backend on
      // the next /auth/logout call or expires naturally in 12h.
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        const msg = encodeURIComponent("Your application access has been deactivated by an admin.");
        window.location.replace(`/login?deactivated=1&msg=${msg}`);
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiError(err) {
  // Axios sets err.message = "Network Error" when fetch() fails without any
  // response — usually a stale service worker, CORS block, or actual
  // offline. Surface an actionable message instead of the raw string.
  if (err?.message === "Network Error" && !err?.response) {
    return "Can't reach the server — please refresh the page or check your connection.";
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join("; ");
  if (detail?.msg) return detail.msg;
  return JSON.stringify(detail);
}

export default api;
